import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import APP_CONFIG from "./app.config";
import { initWebVitals } from "./utils/webVitals";
import { initSentry } from "./sentry.config";
import { UserProvider } from "./contexts/UserContext";
import { SnackbarProvider } from "./contexts/SnackbarProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import NotificationsListener from "./components/NotificationsListener";
import { ThemeProvider, CssBaseline } from "@mui/material";
import theme from "./theme";
import { initFirebaseMessaging, initFirestoreOfflinePersistence } from './services/firebase';
import { queryClient } from "./queryClient";
import { HelpProvider } from './features/help/context/HelpContext';
import App from "./App";
import "./index.css";

document.title = APP_CONFIG.appName;

const scheduleAfterFirstRender = (task: () => void) => {
  window.setTimeout(task, 0);
};

async function initializeAncillaryServices() {
  try {
    await initFirestoreOfflinePersistence();
  } catch (error) {
    console.error("❌ Erreur lors de l'initialisation de la persistence Firestore :", error);
  }

  try {
    await initFirebaseMessaging();
  } catch (error) {
    console.error("❌ Erreur SW FCM ou token :", error);
  }
}

function bootstrap() {
  const environment = import.meta.env.MODE || 'development';
  initSentry(environment);
  initWebVitals();

  const rootElement = document.getElementById("root");
  if (!rootElement) return;

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <ErrorBoundary>
            <SnackbarProvider>
              <NotificationsListener />
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <HelpProvider>
                  <App />
                </HelpProvider>
              </ThemeProvider>
            </SnackbarProvider>
          </ErrorBoundary>
        </UserProvider>
        {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </QueryClientProvider>
    </React.StrictMode>
  );

  scheduleAfterFirstRender(() => {
    void initializeAncillaryServices();
  });
}

bootstrap();
